import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ModalPortal from '../UI/ModalPortal';
import { X, UserCheck, UserX, TrendingUp, ExternalLink } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { Loader } from '../UI/LoadingSpinner';
import StatCard from '../Dashboard/StatCard';
import { ChartCard, TrendLineChart, DonutChart, GroupedBarChart } from '../../utils/chartComponents';
import { Pagination } from '@anyemp/ui-kit';
import StatisticsTable from './StatisticsTable';

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ManagerStartedDetailsModal = ({ manager, filters, onClose }) => {
  const location = useLocation();

  const startedTalentRowKey = useCallback(
    (talent) => talent.id ?? `${talent.full_name}-${talent.started_at}`,
    []
  );

  const recentStartedColumns = useMemo(() => [
    {
      key: 'full_name',
      label: 'Talent Name',
      render: (talent) => talent.id ? (
        <Link
          to={`/talents/${talent.id}`}
          state={{ from: location.pathname + location.search }}
          className="flex items-center gap-1 text-primary-600 visited:text-purple-700 dark:visited:text-purple-400 hover:text-primary-700"
        >
          {talent.full_name || `Talent #${talent.id}`}
          <ExternalLink className="h-3 w-3" />
        </Link>
      ) : (
        <span className="text-gray-900 dark:text-gray-100">
          {talent.full_name || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'from_status',
      label: 'From Status',
      render: (talent) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{talent.from_status || '—'}</span>
      ),
    },
    {
      key: 'started_at',
      label: 'Start Date',
      render: (talent) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">{formatDate(talent.started_at)}</span>
      ),
    },
    {
      key: 'churned',
      label: '2-Week Status',
      render: (talent) => talent.churned
        ? <span className="text-sm font-medium text-red-500 dark:text-red-400">Churned</span>
        : <span className="text-sm font-medium text-green-600 dark:text-green-400">Retained</span>,
    },
  ], [location.pathname, location.search]);

  const [details, setDetails] = useState(null);
  const [summary, setSummary] = useState(null); // кеш агрегатов (total, timeline, retention)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
    setSummary(null);
  }, [manager.manager_id]);

  useEffect(() => {
    fetchDetails();
  }, [manager.manager_id, page]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const isFirstPage = page === 1;
      const params = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        page,
        limit: 20,
        ...(isFirstPage ? {} : { page_only: true, total_hint: summary?.total_started ?? summary?.pagination?.total })
      };

      const response = await statisticsService.getManagerStartedDetails(manager.manager_id, params);

      if (response.data) {
        if (isFirstPage) {
          setSummary(response.data);
        }
        setDetails(response.data);
      }
    } catch (err) {
      console.error('Error fetching started details:', err);
      setError(err.message || 'Failed to load details');
      toast.error('Failed to load started details');
    } finally {
      setLoading(false);
    }
  };


  const formatTimelineDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <ModalPortal onClose={onClose} ariaLabel="Manager started details">
      <div className="flex max-h-[calc(100dvh-1.5rem)] w-full min-w-0 max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800 sm:max-h-[calc(100dvh-4rem)]">
        {/* Header */}
        <div className="flex min-w-0 shrink-0 flex-wrap items-start justify-between gap-3 border-b border-gray-200 p-4 dark:border-gray-700 sm:p-6">
          <div className="flex min-w-0 items-center gap-3">
            <UserCheck className="h-6 w-6 text-primary-600" />
            <div>
              <h2 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
                Start Statistics
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {manager.manager?.name || 'Unknown Manager'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600 dark:text-red-400">
              {error}
            </div>
          ) : details ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  title="Total Start"
                  value={(summary || details).total_started || 0}
                  icon={UserCheck}
                  color="green"
                />
                <StatCard
                  title="Retained"
                  value={(summary || details).retained_count ?? ((summary || details).total_started || 0)}
                  icon={UserCheck}
                  color="green"
                />
                <StatCard
                  title="Churned"
                  value={(summary || details).churned_count || 0}
                  icon={UserX}
                  color="red"
                />
                <StatCard
                  title="Retention Rate"
                  value={`${(summary || details).retention_rate ?? 100}%`}
                  icon={TrendingUp}
                  color="blue"
                />
              </div>

              {/* Timeline Chart */}
              {(summary || details).timeline && (summary || details).timeline.length > 0 && (
                <ChartCard title="Start Timeline" icon={TrendingUp}>
                  <TrendLineChart
                    data={(summary || details).timeline.map(t => ({
                      date: formatTimelineDate(t.date),
                      count: t.count
                    }))}
                    dataKey="count"
                    color="#10B981"
                    name="Talents Started"
                    height={280}
                  />
                </ChartCard>
              )}

              {/* Retention Charts */}
              {(summary || details).total_started > 0 && (
                <ChartCard title="Retention after 14 days" icon={UserCheck}>
                  <div className="flex flex-col md:flex-row gap-6 items-center">
                    <div className="w-full md:w-1/2">
                      <DonutChart
                        data={[
                          { name: 'Retained', value: (summary || details).retained_count ?? ((summary || details).total_started - ((summary || details).churned_count || 0)), color: '#10B981' },
                          { name: 'Churned',  value: (summary || details).churned_count || 0, color: '#EF4444' }
                        ]}
                        height={220}
                      />
                    </div>
                    <div className="w-full md:w-1/2">
                      <GroupedBarChart
                        data={[{
                          name: '',
                          Retained: (summary || details).retained_count ?? ((summary || details).total_started - ((summary || details).churned_count || 0)),
                          Churned:  (summary || details).churned_count || 0
                        }]}
                        bars={[
                          { key: 'Retained', name: 'Retained', color: '#10B981' },
                          { key: 'Churned',  name: 'Churned',  color: '#EF4444' }
                        ]}
                        height={220}
                      />
                    </div>
                  </div>
                </ChartCard>
              )}

              {/* Recently Started Talents Table */}
              <div className="min-w-0 rounded-lg bg-gray-50 p-4 dark:bg-gray-900 sm:p-6">
                <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
                  Recently Start Talents
                </h3>
                <StatisticsTable
                  columns={recentStartedColumns}
                  data={details.recent_started || []}
                  rowKey={startedTalentRowKey}
                  variant="ghost"
                  emptyState={
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No recently start talents found
                    </div>
                  }
                  footer={details?.pagination && details.pagination.total_pages > 1 ? (
                    <Pagination
                      currentPage={page}
                      totalPages={details.pagination.total_pages}
                      totalItems={details.pagination.total}
                      itemsPerPage={20}
                      onPageChange={setPage}
                      showResults="range"
                    />
                  ) : null}
                />
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-gray-200 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] dark:border-gray-700 sm:p-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ManagerStartedDetailsModal;
